import { useEffect, useRef, useState } from "preact/hooks";
import { Icon, type IconName } from "../ui/icon";
import { useMenuKeys } from "../ui/menu_keys";
import { PopoverPanel, usePopover } from "../ui/popover";
import { ACTION_ROW, Button, TextButton } from "./controls";
import type { Labels } from "./labels";
import { ACTION } from "./styles";

export type MenuUrls = { pdf: string; clone: string; share: string; cloneConfirm: string };

const ITEM = "flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-bold text-ink hover:bg-surface-sunken";

// A POST like the old sheet's clone link: Rails' CSRF token rides along and the answer is a normal redirect. `method`
// "delete" is Rails' own override.
export function postForm(url: string, method?: "delete") {
	const form = document.createElement("form");
	form.method = "post";
	form.action = url;
	if (method) {
		const override = document.createElement("input");
		override.type = "hidden";
		override.name = "_method";
		override.value = method;
		form.append(override);
	}
	const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
	if (token) {
		const field = document.createElement("input");
		field.type = "hidden";
		field.name = "authenticity_token";
		field.value = token;
		form.append(field);
	}
	document.body.append(form);
	form.submit();
}

function Item({ icon, label, hint, ...rest }: { icon: IconName; label: string; hint?: string } & ({ href: string } | { onClick: () => void })) {
	const content = (
		<>
			<Icon name={icon} class="h-4 w-4 text-ink-muted" />
			<span class="flex flex-col">
				{label}
				{hint && <span class="text-xs font-normal text-ink-muted">{hint}</span>}
			</span>
		</>
	);
	return "href" in rest ? (
		<a role="menuitem" class={ITEM} href={rest.href} target="_blank" rel="noopener" data-turbo="false">
			{content}
		</a>
	) : (
		<button type="button" role="menuitem" class={ITEM} onClick={rest.onClick}>
			{content}
		</button>
	);
}

function Share({ url, labels }: { url: string; labels: Labels }) {
	const [copied, setCopied] = useState(false);
	const [failed, setFailed] = useState(false);
	const input = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (!copied) return undefined;
		const timer = setTimeout(() => setCopied(false), 2000);
		return () => clearTimeout(timer);
	}, [copied]);
	const copy = () => {
		const done = () => {
			setFailed(false);
			setCopied(true);
		};
		const refused = () => {
			input.current?.select();
			setFailed(true);
		};
		if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, refused);
		else refused();
	};
	return (
		<div class="flex flex-col gap-2 p-3 text-sm">
			<b class="cs-title text-[15px]">{labels.header.share_title}</b>
			<div class="flex flex-wrap items-center gap-2">
				<input
					ref={input}
					readOnly
					value={url}
					aria-label={labels.header.share_link}
					class="min-w-0 flex-1 rounded-lg border border-line-strong bg-surface-sunken px-2 py-1.5 text-link"
					onFocus={(event) => event.currentTarget.select()}
				/>
				<Button tone="alt" icon="copy" size="sm" label={copied ? labels.header.copied : labels.header.copy} onClick={copy} />
			</div>
			{failed && <p role="status">{labels.header.copy_failed}</p>}
			<p>{labels.header.share_description}</p>
			<p class="text-ink-muted">{labels.header.share_edit_note}</p>
		</div>
	);
}

// The sheet's ⋯ menu: the PDF in a new tab, a clone confirmed in the menu, and the share link.
// `levelUp`, for a viewer who has Level Up and a character below the last level: what the menu item says it lacks (the XP) and what opens the popup.
export type LevelUpEntry = { hint: string | null; open: (opener: HTMLElement | null) => void };

export function SheetMenu({ urls, labels, levelUp }: { urls: MenuUrls; labels: Labels; levelUp?: LevelUpEntry | null }) {
	const popover = usePopover<HTMLButtonElement>();
	const [view, setView] = useState<"menu" | "clone" | "share">("menu");
	useMenuKeys(popover, view === "menu");
	const panel = popover.panel;
	const from = useRef<"menu" | "clone" | "share">("menu");

	useEffect(() => {
		if (!popover.open) setView("menu");
	}, [popover.open]);

	// Each view hands focus to its first control; back in the menu, to the item that opened the view just left.
	useEffect(() => {
		if (!popover.open) {
			from.current = "menu";
			return;
		}
		const root = panel.current;
		const first = {
			menu: root?.querySelectorAll<HTMLElement>("[role=menuitem]")[from.current === "clone" ? 1 : from.current === "share" ? 2 : 0],
			clone: root?.querySelector<HTMLElement>("button"),
			share: root?.querySelector<HTMLElement>("input"),
		}[view];
		first?.focus();
		from.current = view;
	}, [popover.open, view, panel]);

	return (
		<>
			<button
				ref={popover.trigger}
				type="button"
				class={ACTION}
				aria-label={labels.header.more}
				aria-haspopup="dialog"
				aria-expanded={popover.open}
				aria-controls={popover.open ? popover.id : undefined}
				onClick={popover.toggle}
			>
				<Icon name="ellipsis" class="h-[18px] w-[18px] rotate-90" />
			</button>
			{popover.open && (
				<PopoverPanel popover={popover} label={labels.header.more} class="w-64 overflow-hidden py-1">
					{view === "menu" && (
						<div role="menu" aria-label={labels.header.more}>
							<Item icon="sheet" label={labels.header.pdf} href={urls.pdf} />
							<Item icon="copy" label={labels.header.clone} onClick={() => setView("clone")} />
							<Item icon="share" label={labels.header.share} onClick={() => setView("share")} />
							{levelUp && (
								<Item
									icon="levelUp"
									label={labels.level_up.title}
									hint={levelUp.hint ?? undefined}
									onClick={() => {
										popover.close();
										levelUp.open(popover.trigger.current);
									}}
								/>
							)}
						</div>
					)}
					{view === "clone" && (
						<div class="flex flex-col gap-2 p-3 text-sm">
							<p>{urls.cloneConfirm}</p>
							<div class={ACTION_ROW}>
								<Button
									tone="primary"
									icon="copy"
									label={labels.header.clone}
									onClick={(button) => {
										button.disabled = true;
										postForm(urls.clone);
									}}
								/>
								<TextButton label={labels.edit.cancel} onClick={() => setView("menu")} />
							</div>
						</div>
					)}
					{view === "share" && <Share url={urls.share} labels={labels} />}
				</PopoverPanel>
			)}
		</>
	);
}
